<script setup lang="ts">
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue"
import { MoveAlbumPosition, useMoveAlbumPosition, useReorderAlbums } from "@/api"
import { AlbumCard } from "@/components"
import { useTranslation } from "@/composables"
import { Album } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useToast } from "../ui/ToastReka/useToast"

interface AlbumListProps {
  albums: Album[]
  isLoading: boolean
  page: number
  perPage: number
  totalAlbums?: number
}

const props = defineProps<AlbumListProps>()

const emit = defineEmits<{
  showLinks: [album: Album]
  showExport: [album: Album]
}>()

const eventVaultStore = useEventVaultStore()

const queryClient = useQueryClient()

const { t } = useTranslation()

const { mutate } = useReorderAlbums()

const { mutate: mutateMoveAlbumPosition, isPending: isMoveAlbumPositionLoading } = useMoveAlbumPosition()

const { error } = useToast()

const isLoading = computed(() => props.isLoading || isMoveAlbumPositionLoading.value)

const internalAlbums = ref<Album[]>([])

const movedAlbumId = ref<number | null>(null)

watchEffect(() => {
  internalAlbums.value = props.albums
})

function flashAlbum(id?: number) {
  if (!id)
    return

  movedAlbumId.value = id
  setTimeout(() => movedAlbumId.value = null, 500)
}

const cleanupMonitor = ref<(() => void) | null>(null)

function optimisticSort(albums: Album[], id: number, to: number): Album[] {
  const clonedAlbums = albums.map(a => (new Album(a)))

  const album = clonedAlbums.find(a => a.id === id)

  if (!album) {
    return clonedAlbums
  }

  const oldIndex = clonedAlbums.map(a => a.id).indexOf(id)

  const [movedAlbum] = clonedAlbums.splice(oldIndex, 1)

  clonedAlbums.splice(to - 1, 0, movedAlbum)

  clonedAlbums.forEach((album, i) => {
    album.displayOrder = i + 1
  })

  return clonedAlbums
}

function handleMove(id: number, to: number) {
  if (!eventVaultStore.eventVaultId) {
    return
  }

  const currentIndex = internalAlbums.value.findIndex(a => a.id === id)
  const currentPosition = currentIndex + 1

  if (currentPosition === to) {
    return
  }

  internalAlbums.value = optimisticSort(internalAlbums.value, id, to)

  flashAlbum(id)

  mutate({
    eventVaultId: eventVaultStore.eventVaultId,
    position: { albumId: id, position: to },
    page: props.page,
    perPage: props.perPage,
  }, {
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ["listAlbums"] })
    },
    async onError() {
      error({ title: t("album_list.error_toast.title"), description: t("album_list.error_toast.desc") })

      await queryClient.invalidateQueries({ queryKey: ["listAlbums"] })
    },
  })
}

onMounted(() => {
  if (cleanupMonitor.value) {
    cleanupMonitor.value()
    cleanupMonitor.value = null
  }

  cleanupMonitor.value = monitorForElements({
    onDrop: ({ source, location }) => {
      const id: unknown = source.data?.album
      const to: unknown = location.current.dropTargets[0]?.data?.location

      if ((!id || typeof id !== "number") || (!to || typeof to !== "number")) {
        return
      }

      handleMove(id, to)
    },
  })
})

onUnmounted(() => {
  if (cleanupMonitor.value) {
    cleanupMonitor.value()
    cleanupMonitor.value = null
  }
})

function onShowLinks(album?: Album) {
  if (!album) {
    return
  }

  emit("showLinks", album)
}

function onShowExport(album?: Album) {
  if (!album) {
    return
  }

  emit("showExport", album)
}

function getCanMoveToStart(index: number): boolean {
  const absolutePosition = (props.page - 1) * props.perPage + (index + 1)
  return absolutePosition !== 1
}

function getCanMoveToEnd(index: number): boolean {
  const absolutePosition = (props.page - 1) * props.perPage + (index + 1)
  const totalAlbums = props.totalAlbums
  return absolutePosition !== totalAlbums
}

function movePosition(position: MoveAlbumPosition, album?: Album) {
  if (album?.id === undefined || !eventVaultStore.eventVaultId) {
    return
  }

  mutateMoveAlbumPosition({ eventVaultId: eventVaultStore.eventVaultId, albumId: album.id, position }, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["listAlbums"] })
      flashAlbum(album?.id)
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ["listAlbums"] })
      error({ title: t("album_list.error_toast.title"), description: t("album_list.error_toast.desc") })
    },
  })
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <AlbumCard
      v-for="(album, index) in internalAlbums"
      :key="album.id"
      :album="album"
      :is-loading="isLoading"
      is-draggable
      :location="index + 1"
      :is-flashing="album.id === movedAlbumId"
      :can-move-to-start="getCanMoveToStart(index)"
      :can-move-to-end="getCanMoveToEnd(index)"
      @move="(albumId, location) => handleMove(albumId, location)"
      @move-to-start="(album) => movePosition(MoveAlbumPosition.Top, album)"
      @move-to-end="(album) => movePosition(MoveAlbumPosition.Bottom, album)"
      @show-links="onShowLinks"
      @show-export="onShowExport"
    />
  </div>
</template>
